/**
 * Источник значения в строке формы (US-39 FR-8, FR-10): бейдж слоя, тумблер
 * источника и подпись «введено вручную». Общие у скалярного показателя и у
 * таблицы — у неё выбор делается по таблице целиком (FR-15).
 */
import { useTranslation } from 'react-i18next';

import { cn } from '../../../../lib/cn';
import type {
  PassportSource,
  SourceOption,
} from '../../../../requests/input_data.request';
import { pick, type Lang } from '../formState';

/**
 * Цвет бейджа по слою. Подпись приходит с сервера — экран выбирает только
 * оформление; незнакомый слой (новый источник на бэке) получает нейтральное.
 */
const LAYER_CLASS: Record<string, string> = {
  bns: 'bg-status-info/10 text-status-info',
  snp_monitoring: 'bg-brand-50 text-brand-700',
  treasury: 'bg-chart-indigo/10 text-chart-indigo',
  manual: 'bg-surface-muted text-text-muted',
  none: 'border border-dashed border-surface-border text-text-faint',
};

export const SourceBadge = ({
  source,
  lang,
  testId,
}: {
  source: PassportSource | null | undefined;
  lang: Lang;
  testId: string;
}) => {
  if (source === null || source === undefined) return null;
  const label = pick(source.label_ru, source.label_kz, lang);
  return (
    <span
      title={label}
      data-testid={testId}
      data-layer={source.layer}
      className={cn(
        'inline-flex max-w-56 shrink-0 items-center truncate rounded-full px-2.5 py-1 text-xs font-semibold',
        LAYER_CLASS[source.layer] ?? LAYER_CLASS.manual,
      )}
    >
      {label}
    </span>
  );
};

export const SourceSelect = ({
  options,
  value,
  lang,
  testId,
  onChange,
}: {
  options: SourceOption[];
  value: string | null;
  lang: Lang;
  testId: string;
  onChange: (mode: string) => void;
}) => {
  const { t } = useTranslation();
  // FR-10.1: только варианты, объявленные у показателя; без них тумблера нет.
  if (options.length === 0) return null;
  return (
    <select
      value={value ?? ''}
      aria-label={t('adminInput.sourceLabel')}
      data-testid={testId}
      onChange={(event) => onChange(event.target.value)}
      className="h-9 w-44 shrink-0 rounded-md border border-surface-border bg-white px-2 text-xs"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {pick(option.label_ru, option.label_kz, lang)}
        </option>
      ))}
    </select>
  );
};

/** Дата API `YYYY-MM-DD` → `ДД.ММ.ГГГГ`. */
const fmtDate = (iso: string): string => {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
};

/**
 * Период данных ручного ввода: целый год — просто годом, иначе диапазоном.
 * `null` — сервер периода не прислал.
 */
export const periodLabel = (
  from: string | null | undefined,
  to: string | null | undefined,
): string | null => {
  if (!from && !to) return null;
  if (from && to && from.endsWith('-01-01') && to.endsWith('-12-31')
    && from.slice(0, 4) === to.slice(0, 4)) {
    return from.slice(0, 4);
  }
  return [from, to]
    .filter((part): part is string => Boolean(part))
    .map(fmtDate)
    .join(' — ');
};

/**
 * Подпись «введено вручную» — второй строкой, мелко (FR-8.1, FR-13): что ввёл
 * оператор, за какой период и кто. Видна и тогда, когда под виджет уходит
 * значение источника: переключение ничего не стирает.
 *
 * Автор приходит Keycloak-идентификатором, а не именем, — подписываем его,
 * только когда это сам смотрящий («вами»); чужой идентификатор оператору
 * ничего не скажет.
 */
export const ManualNote = ({
  value,
  periodFrom,
  periodTo,
  updatedBy,
  currentUserId,
  testId,
  className,
}: {
  /** Готовая строка «что введено» (значение либо число строк); `null` — не показывать. */
  value: string | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  updatedBy?: string | null;
  currentUserId: string | null;
  testId: string;
  className?: string;
}) => {
  const { t } = useTranslation();
  if (value === null) return null;
  const period = periodLabel(periodFrom, periodTo);
  const parts = [
    t('adminInput.manualNote', { value }),
    period !== null ? t('adminInput.manualPeriod', { value: period }) : null,
    updatedBy && updatedBy === currentUserId ? t('adminInput.manualByYou') : null,
  ].filter((part): part is string => part !== null);
  return (
    <p
      data-testid={testId}
      className={cn('text-xs leading-snug text-text-faint', className)}
    >
      {parts.join(' · ')}
    </p>
  );
};
